import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MetricCard } from "../components/MetricCard";
import { useBillingAuditLogs } from "@/hooks/use-billing-audit-logs";
import { FileText, Mail, AlertTriangle, CheckCircle, Clock, DollarSign } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getActionLabel } from "@/lib/audit/action-labels";
import { CATEGORY_COLORS } from "@/lib/audit/action-categories";

export function BillingTab() {
  const { data, isLoading } = useBillingAuditLogs();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-24"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-16"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { automationLogs, emailLogs } = data;

  // Calculate KPIs
  const totalAutomations = automationLogs.length;
  const totalInvoicesCreated = automationLogs.reduce((sum, log) => sum + (log.invoices_created || 0), 0);
  const totalInvoicesSkipped = automationLogs.reduce((sum, log) => sum + (log.invoices_skipped || 0), 0);
  const totalAmountCents = automationLogs.reduce((sum, log) => sum + (log.total_amount_cents || 0), 0);
  const emailsSent = emailLogs.filter(log => log.action === 'billing.email_sent').length;
  const emailsFailed = emailLogs.filter(log => log.action === 'billing.email_failed').length;
  const successfulAutomations = automationLogs.filter(log => 
    !log.errors || !Array.isArray(log.errors) || log.errors.length === 0
  ).length;
  const successRate = totalAutomations > 0 ? Math.round((successfulAutomations / totalAutomations) * 100) : 100;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Execuções de Automação"
          value={totalAutomations}
          description="Últimas 24h"
          icon={Clock}
        />
        <MetricCard
          title="Faturas Geradas"
          value={totalInvoicesCreated}
          description="Total criado"
          icon={FileText}
        />
        <MetricCard
          title="Taxa de Sucesso"
          value={`${successRate}%`}
          description="Automações bem-sucedidas"
          icon={CheckCircle}
        />
        <MetricCard
          title="Valor Total"
          value={`R$ ${(totalAmountCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          description="Valor faturado"
          icon={DollarSign}
        />
        <MetricCard
          title="Emails Enviados"
          value={emailsSent}
          description="Notificações de fatura"
          icon={Mail}
        />
        <MetricCard
          title="Emails Falhados"
          value={emailsFailed}
          description="Envios com erro"
          icon={AlertTriangle}
        />
        <MetricCard
          title="Faturas Puladas"
          value={totalInvoicesSkipped}
          description="Não criadas"
          icon={AlertTriangle}
        />
      </div>

      {/* Automation Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Timeline de Automações de Faturamento</CardTitle>
        </CardHeader>
        <CardContent>
          {automationLogs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nenhuma automação executada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {automationLogs.map((log) => {
                const hasErrors = log.errors && Array.isArray(log.errors) && log.errors.length > 0;
                const colors = hasErrors ? CATEGORY_COLORS.error : CATEGORY_COLORS.billing;
                
                return (
                  <div
                    key={log.id}
                    className={`p-4 rounded-lg border ${colors.border} ${colors.bg}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={colors.badge} variant="outline">
                            {log.execution_type}
                          </Badge>
                          {hasErrors ? (
                            <Badge variant="destructive">Com Erros</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">Sucesso</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">Empresas:</span>
                            <span className="font-medium ml-1">{log.companies_found}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Criadas:</span>
                            <span className="font-medium ml-1">{log.invoices_created}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Puladas:</span>
                            <span className="font-medium ml-1">{log.invoices_skipped}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Valor:</span>
                            <span className="font-medium ml-1">
                              R$ {(log.total_amount_cents / 100).toLocaleString('pt-BR')}
                            </span>
                          </div>
                        </div>
                        {hasErrors && (
                          <div className="mt-3 p-2 bg-red-50 dark:bg-red-950 rounded text-xs">
                            <p className="font-medium text-red-900 dark:text-red-100 mb-1">Erros:</p>
                            <ul className="list-disc list-inside text-red-700 dark:text-red-300">
                              {(log.errors as string[]).map((error, idx) => (
                                <li key={idx}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        {format(new Date(log.executed_at), 'dd/MM/yy HH:mm', { locale: ptBR })}
                        {log.execution_duration_ms && (
                          <div className="mt-1">{log.execution_duration_ms}ms</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Logs de Email de Faturas</CardTitle>
        </CardHeader>
        <CardContent>
          {emailLogs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nenhum email enviado</p>
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailLogs.map((log) => {
                    const isSuccess = log.action === 'billing.email_sent';
                    const colors = isSuccess ? CATEGORY_COLORS.billing : CATEGORY_COLORS.error;
                    
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">
                          {format(new Date(log.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          <Badge className={colors.badge} variant="outline">
                            {getActionLabel(log.action)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.company_id ? `${log.company_id.substring(0, 8)}...` : '-'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.meta && typeof log.meta === 'object' && 'recipient_email' in log.meta
                            ? String(log.meta.recipient_email)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
